import {
    Card,
    Flex,
    Grid,
    Space,
    Table, TableProps,
    Typography,
} from 'antd';
import {
    CSSProperties,
    useEffect, useMemo, useState,
} from 'react';

import {
    ColumnKey, PAGE_SIZE_OPTIONS,
} from '@/constants';
import { useCustomParams } from '@/hooks';
import { withMobileTableColumns } from '@/utils';

import { SearchInput } from './SearchInput';

type DefaultTableProps = {
    title?: string | null;
    pagination?: boolean;
    defaultPageSize?: number;
    columns: TableProps<any>['columns'];
    dataSource: TableProps<any>['dataSource'];
    total: number;
    loading: boolean;
    hasSearch?: boolean;
    renderCreateButton?: () => React.ReactNode;
    expandable?: TableProps<any>['expandable'];
    rowKey?: string | ((record: any) => string);
    rowClassName?: (record: any) => string;
    scroll?: TableProps<any>['scroll'];
    enableMobileStack?: boolean;
    tableSize?: string | null;
};

export const DefaultTable = ({
    title,
    pagination = true,
    defaultPageSize,
    hasSearch = true,
    renderCreateButton,
    columns,
    dataSource,
    total,
    loading,
    expandable,
    rowKey = ColumnKey.Id,
    rowClassName,
    scroll = { x: 1000 },
    enableMobileStack = true,
    tableSize = 'middle',
}: DefaultTableProps) => {
    const screens = Grid.useBreakpoint();
    const isMobileTableLayout = screens.lg === false;

    const resolvedColumns = useMemo(
        () =>
            withMobileTableColumns(columns ?? [], {
                enable: enableMobileStack && isMobileTableLayout,
            }),
        [columns, enableMobileStack, isMobileTableLayout],
    );

    const resolvedScroll = useMemo(() => {
        if (isMobileTableLayout && enableMobileStack) {
            return undefined;
        }
        return scroll;
    }, [isMobileTableLayout, enableMobileStack, scroll]);

    const { pageSize, currentPage, search, setPageSize, setCurrentPage, setSearch, setOrderColumn, setOrderDirection } = useCustomParams(
        defaultPageSize !== undefined ? { defaultPageSize } : undefined,
    );
    const [searchValue, setSearchValue] = useState(search || '');

    useEffect(() => {
        setSearchValue(search || '');
    }, [search]);

    const handleSearch = (value: string) => {
        setSearch(value || '');
        setCurrentPage(1);
    };

    const handleChange = (value: string) => {
        setSearchValue(value);
        if (!value) {
            handleSearch('');
        }
    };

    const hasToolbar = Boolean(
        hasSearch || renderCreateButton || (title !== null && title !== undefined),
    );

    const cardTitle = hasToolbar ? (
        isMobileTableLayout ? (
            <Flex vertical gap={12} align="stretch" className="default-table-toolbar default-table-toolbar--mobile">
                {title !== null && title !== undefined ? (
                    <Typography.Title
                        level={3}
                        ellipsis={false}
                        style={{ margin: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
                    >
                        {title}
                    </Typography.Title>
                ) : null}
                {hasSearch && (
                    <SearchInput
                        className="search-input-compact"
                        placeholder="Пошук..."
                        value={searchValue}
                        onSearch={handleSearch}
                        onChange={handleChange}
                        disabled={loading}
                    />
                )}
                {renderCreateButton && (
                    <Flex justify="flex-end" style={{ width: '100%' }}>
                        {renderCreateButton()}
                    </Flex>
                )}
            </Flex>
        ) : (
            <Flex justify="space-between" align="center" className="default-table-toolbar">
                {title !== null && title !== undefined ? (
                    <Typography.Title
                        level={3}
                        ellipsis={false}
                        style={{ margin: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
                    >
                        {title}
                    </Typography.Title>
                ) : <div />}
                {(hasSearch || renderCreateButton) && (
                    <Space>
                        {renderCreateButton && renderCreateButton()}
                        {hasSearch && <SearchInput
                            placeholder="Пошук..."
                            value={searchValue}
                            onSearch={handleSearch}
                            onChange={handleChange}
                            disabled={loading}
                        />}
                    </Space>
                )}
            </Flex>
        )
    ) : undefined;

    const cardStyles = useMemo(() => {
        if (!isMobileTableLayout) {
            return undefined;
        }
        return {
            body: { padding: 12 },
            ...(hasToolbar
                ? {
                    header: {
                        paddingTop: 12,
                        paddingBottom: 12,
                        paddingLeft: 12,
                        paddingRight: 12,
                        overflow: 'visible',
                    },
                    title: {
                        display: 'block',
                        overflow: 'visible',
                        whiteSpace: 'normal',
                        textOverflow: 'unset',
                        wordBreak: 'break-word',
                        minWidth: 0,
                        maxWidth: '100%',
                    } satisfies CSSProperties,
                }
                : {}),
        };
    }, [isMobileTableLayout, hasToolbar]);

    return (
        <Card className="default-table-card" title={cardTitle} styles={cardStyles}>
            <Table
                size={tableSize}
                loading={loading}
                dataSource={dataSource}
                rowKey={rowKey}
                columns={resolvedColumns}
                showSorterTooltip={false}
                scroll={resolvedScroll}
                expandable={expandable}
                rowClassName={rowClassName}
                onRow={(record) => {
                    const className = rowClassName ? rowClassName(record) : '';
                    return {
                        className,
                    };
                }}
                pagination={pagination ? {
                    current: currentPage,
                    pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: PAGE_SIZE_OPTIONS,
                    total,
                    showTotal: (total, range) => `${range[0]}-${range[1]} з ${total}`,
                    onChange: (page, size) => {
                        setCurrentPage(page);

                        if (size !== pageSize) {
                            setPageSize(size);
                        }
                    },
                } : false}
                onChange={(_pagination, _filters, sorter) => {
                    if (!Array.isArray(sorter) && sorter.order) {
                    // @ts-ignore
                        setOrderColumn(sorter.field);
                        setOrderDirection(sorter.order === 'ascend' ? 'asc' : 'desc');
                    }
                }}
            />
        </Card>
    );
};
